import { Box, FlexBox } from '@dixa/lib-shared-habla';
import { SuggestionPreviewView } from 'articleSuggestions/components/suggestionPreview';
import { SuggestionRow } from 'articleSuggestions/components/suggestionRow';
import { useCategoryNames } from 'articleSuggestions/hooks/useCategoryNames';
import { useCollectionNames } from 'articleSuggestions/hooks/useCollectionNames';
import { Suggestion } from 'articleSuggestions/utils/toSuggestion';
import { useMemo } from 'react';

import { TestConversation, TestDraft, toTestSuggestion } from './testRun.utils';
import { GapList } from './testRunOutcome';
import styles from './testSettingsModal.module.scss';
import { useTestDraftActions } from './useTestDraftActions';

type TestRunDraftsProps = {
  drafts: readonly TestDraft[];
  selectedKey: string | undefined;
  onSelect: (key: string) => void;
  onChange: (draft: TestDraft) => void;
  onRemove: (key: string) => void;
  conversations: readonly TestConversation[];
  judgedAt: number;
};

export function TestRunDrafts({
  drafts,
  selectedKey,
  onSelect,
  onChange,
  onRemove,
  conversations,
  judgedAt,
}: TestRunDraftsProps): JSX.Element {
  const categoryName = useCategoryNames();
  const collectionIds = useMemo(
    () => drafts.map((draft) => draft.collectionId),
    [drafts],
  );
  const collectionNamesById = useCollectionNames(collectionIds);

  const suggestions = drafts.map((draft) => ({
    draft,
    suggestion: toTestSuggestion(draft, {
      conversations,
      judgedAt,
      collectionNamesById,
      categoryName,
    }),
  }));
  const selected =
    suggestions.find(({ draft }) => draft.key === selectedKey) ??
    suggestions[0];

  return (
    <FlexBox flex="1" minHeight="0" className={styles.panes}>
      <FlexBox
        flexDirection="column"
        flex="0 0 auto"
        width="332px"
        className={styles.list}
      >
        <Box
          as="ul"
          margin="0"
          padding="0"
          className={styles.rows}
          aria-label="Suggestions from the test"
        >
          {suggestions.map(({ suggestion }) => (
            <SuggestionRow
              key={suggestion.id}
              suggestion={suggestion}
              isSelected={suggestion.id === selected?.suggestion.id}
              onSelect={() => onSelect(suggestion.id)}
            />
          ))}
        </Box>
      </FlexBox>

      {selected && (
        <SelectedTestDraft
          key={selected.draft.key}
          draft={selected.draft}
          suggestion={selected.suggestion}
          onChange={onChange}
          onRemove={onRemove}
        />
      )}
    </FlexBox>
  );
}

function SelectedTestDraft({
  draft,
  suggestion,
  onChange,
  onRemove,
}: {
  draft: TestDraft;
  suggestion: Suggestion;
  onChange: (draft: TestDraft) => void;
  onRemove: (key: string) => void;
}) {
  const actions = useTestDraftActions({
    suggestion,
    onRefile: (target) =>
      onChange({
        ...draft,
        // Omitted leaves it as it is; `null` clears it.
        collectionId:
          target.collectionId === undefined
            ? draft.collectionId
            : (target.collectionId ?? undefined),
        categoryId:
          target.categoryId === undefined
            ? draft.categoryId
            : (target.categoryId ?? undefined),
      }),
    onRemoved: () => onRemove(draft.key),
  });

  return (
    <FlexBox flexDirection="column" flex="1" minWidth="0" minHeight="0">
      {draft.gaps.length > 0 && (
        <Box
          flex="0 0 auto"
          maxHeight="30%"
          padding="300 500"
          background="neutral.100"
          className={styles.draftGaps}
        >
          <GapList
            title={draft.gaps.length === 1 ? 'Gap found' : 'Gaps found'}
            gaps={draft.gaps}
            withLinks={false}
          />
        </Box>
      )}
      <SuggestionPreviewView
        suggestion={suggestion}
        actions={actions}
        unsaved
      />
    </FlexBox>
  );
}
